import { useEffect, useId, useRef, useState } from "react";
import {
  EXCHANGE_TIME_ZONE,
  dateKeyInZone,
  formatTime,
  zoneAbbreviation,
  manualLockoutScope,
  manualLockoutBatchMessage,
  newRequestId,
  useConnection,
  useTrading,
  useSettings,
  type ManualLockoutInventoryResult,
  type CommandOutcome,
} from "@helm/core";
import { getAccountView, useAccountView } from "../viewmodel/account";
import { commands, useBusy } from "../trading/control";
import { confirmOrderChoice } from "../trading/hosts";
import { lockoutPresetFor, resolveLockoutUntil, type LockoutChoice } from "@helm/ui";
import { formatInstantWithZone, useDisplayZone } from "../time/useDisplayZone";
import { TradeButton } from "../components/TradeButton";

const CHOICES: ReadonlyArray<readonly [LockoutChoice, string]> = [
  ["rest_of_session", "Rest of session"],
  ["1h", "1 hour"],
  ["2h", "2 hours"],
  ["4h", "4 hours"],
  ["custom", "Custom hours"],
  ["until_next_session", "Until next session"],
  ["until", "Lock out until…"],
];

/** `datetime-local` value for instant `ms` as a wall clock in `zone`. */
function zonedInputValue(ms: number, zone: string): string {
  return `${dateKeyInZone(ms, zone)}T${formatTime(ms, zone, { seconds: false })}`;
}

function useNow(periodMs: number): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setTick((tick) => tick + 1);
    }, periodMs);
    return () => {
      clearInterval(t);
    };
  }, [periodMs]);
  // Read the current clock on every render, including the first newly received lockedUntil.
  return Date.now();
}

/**
 * Personal Lockout (SPEC §10.2, SPEC-ADDENDA A3; Tradesea panel). Builds the domain
 * `LockoutPreset`, sends once after confirmation, and shows the gateway result without
 * optimistically changing the authoritative countdown.
 */
export function LockoutPanel({ lockedUntil }: { lockedUntil: number | null }): React.JSX.Element {
  const id = useId();
  const view = useAccountView();
  const account = view.selection;
  const [scope, setScope] = useState<"selected" | "all">("selected");
  const allAccounts = view.allSelected || scope === "all";
  const privacy = useSettings((s) => s.settings.privacy);
  const accountLabel = allAccounts
    ? "All connected accounts"
    : account
      ? privacy
        ? "Selected account (***)"
        : account
      : "none selected";
  const connected = useConnection((s) => s.status === "connected");
  const key = "manual-lockout-batch";
  const busy = useBusy(key);
  const [confirming, setConfirming] = useState(false);
  const [feedback, setFeedback] = useState<{
    label: string;
    targets: { loginId: string; accountId: string }[];
    response: CommandOutcome;
  } | null>(null);
  const [inventory, setInventory] = useState<ManualLockoutInventoryResult | null>(null);
  const [inventoryMessage, setInventoryMessage] = useState("");
  const [refresh, setRefresh] = useState(0);
  const inventoryBusy = useBusy(`manual-lockout-inventory:${id}`);
  const inventoryRef = useRef(inventory);
  const inventoryRequest = useRef<Promise<CommandOutcome | null> | null>(null);
  const scopeRef = useRef(allAccounts);
  useEffect(() => {
    inventoryRef.current = inventory;
  }, [inventory]);
  useEffect(() => {
    scopeRef.current = allAccounts;
  }, [allAccounts]);
  useEffect(() => {
    let current = true;
    if (connected) {
      // StrictMode mounts effects twice: share the pending read, so its first cleanup cannot
      // discard the only answer while the second read is suppressed as busy.
      const request =
        inventoryRequest.current ??
        commands.run(`manual-lockout-inventory:${id}`, {
          case: "manualLockoutInventoryRequest",
          value: { $typeName: "helm.v1.ManualLockoutInventoryRequest" },
        });
      inventoryRequest.current = request;
      void request.then((response) => {
        if (inventoryRequest.current === request) inventoryRequest.current = null;
        if (!current || !response) return;
        setInventory(response.lockoutInventory ?? null);
        setInventoryMessage(response.lockoutInventory ? "" : response.message || response.code);
      });
    }
    return () => {
      current = false;
    };
  }, [connected, id, refresh]);
  const selection = manualLockoutScope(connected, inventory, account, allAccounts);
  const eligibility = inventoryBusy
    ? { ok: false as const, code: "NOT_AVAILABLE" as const, reason: "Checking current connected accounts…" }
    : selection.gate;
  const now = useNow(lockedUntil !== null ? 1000 : 15_000);
  const [choice, setChoice] = useState<LockoutChoice>("rest_of_session");
  const [hours, setHours] = useState("6");
  const zone = useDisplayZone();
  const [until, setUntil] = useState(() => zonedInputValue(Date.now() + 2 * 3600_000, zone));
  const [flatten, setFlatten] = useState(true);
  const result = lockoutPresetFor(choice, hours, until, zone, now);
  const error = result.ok ? null : result.message;
  const tzLabel = `${zoneAbbreviation(zone, now)} — ${zone}`;

  const active = lockedUntil !== null && lockedUntil > now;
  const gate = error ? { ok: false as const, code: "NOT_AVAILABLE" as const, reason: error } : eligibility;

  async function submit(): Promise<void> {
    if (!result.ok || !gate.ok || !inventory || busy || confirming) return;
    const confirmedInventory = inventory;
    const primaryToken = useTrading.getState().session?.manualLockToken;
    const targets = selection.targets;
    const duration =
      choice === "custom"
        ? `${hours} hours`
        : choice === "until"
          ? formatInstantWithZone(
              Number(result.preset.type === "until" ? result.preset.until_ns / 1_000_000n : 0n),
              zone,
            )
          : (CHOICES.find(([value]) => value === choice)?.[1] ?? choice);
    setConfirming(true);
    try {
      const confirmed = await confirmOrderChoice({
        words: `${accountLabel} (${String(targets.length)} ${targets.length === 1 ? "account" : "accounts"}): ${duration}${flatten ? "; request flatten and cancel orders" : "; keep existing positions and orders"}${privacy ? "" : ` · ${targets.map((t) => `${t.accountId} (${t.loginId})`).join(", ")}`}`,
        anchor: null,
        warning: {
          title: active && !allAccounts ? "Extend personal lockout" : "Set personal lockout",
          confirmLabel: "Set lockout",
          note: "Cannot be shortened or cancelled. Flatten may fail independently; the lockout remains active.",
        },
      });
      if (!confirmed.ok) return;
      if (
        getAccountView().selection !== account ||
        scopeRef.current !== allAccounts ||
        inventoryRef.current !== confirmedInventory ||
        useTrading.getState().session?.manualLockToken !== primaryToken ||
        useConnection.getState().status !== "connected"
      ) {
        setFeedback({
          label: accountLabel,
          targets: [],
          response: {
            ok: false,
            code: "SCOPE_CHANGED",
            message:
              "Account, scope or connection changed. Refresh accounts and confirm a new lockout. Nothing was sent.",
            orderId: "",
          },
        });
        return;
      }
      setFeedback({
        label: accountLabel,
        targets,
        response: {
          ok: false,
          code: "WAITING",
          message: "Waiting for the gateway. Nothing will be retried automatically.",
          orderId: "",
        },
      });
      const response = await commands.run(key, {
        case: "manualLockoutBatch",
        value: manualLockoutBatchMessage(
          confirmedInventory,
          targets,
          allAccounts,
          newRequestId(),
          result.preset,
          flatten,
        ),
      });
      if (response) setFeedback({ label: accountLabel, targets, response });
    } finally {
      setConfirming(false);
    }
  }

  // The picked wall time is interpreted in the display zone; show the instant in that zone and CT.
  let resolved: string | null = null;
  let notice: string | null = null;
  if (choice === "until") {
    const r = resolveLockoutUntil(until, zone, now);
    if (r.ok) {
      const ct =
        zone === EXCHANGE_TIME_ZONE ? "" : ` · ${formatInstantWithZone(r.untilMs, EXCHANGE_TIME_ZONE)}`;
      resolved = `Ends ${formatInstantWithZone(r.untilMs, zone)}${ct}`;
      notice = r.notice;
    }
  }

  return (
    <div className="lockout">
      <div className="panel-heading" id={`${id}-h`}>
        Personal Lockout
      </div>
      <p className="muted">Account: {accountLabel}</p>
      <label className="field">
        <span>Lockout scope</span>
        <select
          aria-label="Lockout scope"
          value={allAccounts ? "all" : "selected"}
          disabled={busy || confirming}
          onChange={(e) => {
            setScope(e.target.value === "all" ? "all" : "selected");
          }}
        >
          <option value="selected" disabled={view.allSelected}>
            Selected account
          </option>
          <option value="all">All connected accounts</option>
        </select>
      </label>
      <p className="muted">
        TradePrizm operational locks only. Disconnected logins are not covered or connected automatically.
      </p>
      <button
        type="button"
        className="btn"
        disabled={!connected || inventoryBusy || busy || confirming}
        onClick={() => {
          setInventory(null);
          setRefresh((n) => n + 1);
        }}
      >
        Refresh accounts
      </button>
      {inventoryMessage && (
        <p role="status">
          {privacy ? "Account inventory unavailable. Refresh to check coverage." : inventoryMessage}
        </p>
      )}
      <ul aria-label="Lockout account coverage">
        {(allAccounts ? (inventory?.targets ?? []) : selection.targets).map((target, i) => (
          <li key={`${target.loginId}:${target.accountId}`}>
            {privacy ? `Account ${String(i + 1)} (***)` : `${target.accountId} (${target.loginId})`} — READY
          </li>
        ))}
        {inventory?.unavailable.map((item, i) => (
          <li key={`${item.loginId}:${String(i)}`}>
            {privacy ? `Login ${String(i + 1)} (***)` : item.loginId} — unavailable:{" "}
            {privacy ? item.code : item.message || item.code}
          </li>
        ))}
      </ul>
      {active && !allAccounts && (
        <div className="lockout-active">
          <div className="panel-heading">Locked Out – Time Remaining</div>
          <div className="lockout-countdown num">{countdown(lockedUntil - now)}</div>
          <p className="muted">Ends {formatInstantWithZone(lockedUntil, zone)}.</p>
        </div>
      )}
      <p className="muted lockout-lede">Block new orders for a while. Pick how long:</p>
      <div role="radiogroup" aria-labelledby={`${id}-h`} className="lockout-choices">
        {CHOICES.map(([v, label]) => (
          <label key={v} className="radio-row">
            <input
              type="radio"
              name={`${id}-choice`}
              value={v}
              checked={choice === v}
              onChange={() => {
                setChoice(v);
              }}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
      {choice === "custom" && (
        <label className="field">
          <span>Enter Custom hours</span>
          <input
            inputMode="numeric"
            value={hours}
            aria-invalid={error !== null}
            aria-describedby={error ? `${id}-err` : undefined}
            onChange={(e) => {
              setHours(e.target.value);
            }}
          />
        </label>
      )}
      {choice === "until" && (
        <label className="field">
          <span>Lock out until ({tzLabel})</span>
          <input
            type="datetime-local"
            value={until}
            min={zonedInputValue(now, zone)}
            aria-invalid={error !== null}
            aria-describedby={error ? `${id}-err` : resolved ? `${id}-res` : undefined}
            onChange={(e) => {
              setUntil(e.target.value);
            }}
          />
        </label>
      )}
      {error && (
        <p id={`${id}-err`} className="field-error" role="alert">
          {error}
        </p>
      )}
      {resolved && (
        <p id={`${id}-res`} className="muted lockout-resolved">
          {resolved}
        </p>
      )}
      {notice && <p className="lockout-warning">{notice}</p>}
      <label className="check-row">
        <input
          type="checkbox"
          checked={flatten}
          onChange={(e) => {
            setFlatten(e.target.checked);
          }}
        />
        <span>Flatten positions and cancel orders when the lockout starts</span>
      </label>
      {flatten && (
        <p className="lockout-warning" role="note">
          Flatten and cancellation will be requested for each account in the confirmed scope. Check positions
          and orders for completion; a refusal does not undo the lockout.
        </p>
      )}
      <p className="muted lockout-note">
        A lockout can only be extended — it can’t be shortened or cancelled.
      </p>
      {!eligibility.ok && (
        <p className="muted" role="status">
          {eligibility.reason}
        </p>
      )}
      {feedback && (
        <div role="status" aria-label="Lockout results">
          <strong>{privacy ? "Lockout request" : feedback.label}</strong>
          <p>
            <strong>{feedback.response.code}</strong>{" "}
            {privacy && feedback.response.lockoutBatch ? "" : feedback.response.message}
          </p>
          {feedback.response.code !== "WAITING" &&
            feedback.targets.map((target, i) => {
              const matches =
                feedback.response.lockoutBatch?.results.filter(
                  (item) => item.loginId === target.loginId && item.result?.accountId === target.accountId,
                ) ?? [];
              const result = matches.length === 1 ? matches[0]?.result : undefined;
              return (
                <div key={`${target.loginId}:${target.accountId}`}>
                  <strong>
                    {privacy ? `Account ${String(i + 1)} (***)` : `${target.accountId} (${target.loginId})`}
                  </strong>
                  <p>
                    {result?.applied ? "Lock applied" : "Lock not confirmed"} ·{" "}
                    {result?.code ?? "OUTCOME_UNKNOWN"}
                  </p>
                  {result?.applied && <p>Ends {formatInstantWithZone(Number(result.untilMs), zone)}.</p>}
                  <p>
                    Flatten: {result?.flattenStatus || "unknown"}. Persistence:{" "}
                    {result?.persistence || "unknown"}.
                  </p>
                  {!privacy && <p>{result?.message}</p>}
                </div>
              );
            })}
          {feedback.response.lockoutBatch?.unavailable.map((item, i) => (
            <p key={`${item.loginId}:${String(i)}`}>
              {privacy ? `Login ${String(i + 1)} (***)` : item.loginId}:{" "}
              {privacy ? item.code : item.message || item.code}
            </p>
          ))}
          <p>Check each account's lockout, positions and orders. Nothing is retried automatically.</p>
        </div>
      )}
      <TradeButton
        semantics="Starts or extends the confirmed accounts' lockouts"
        tone="sell"
        size="lg"
        gate={gate}
        busy={busy || confirming}
        onPress={() => {
          void submit();
        }}
      >
        {active && !allAccounts ? "EXTEND LOCKOUT" : "SET LOCKOUT"}
      </TradeButton>
    </div>
  );
}

function countdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
}
